import { useContext, useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { alpha } from '@mui/material';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import InventoryIcon from '@mui/icons-material/Inventory';
import PercentIcon from '@mui/icons-material/Percent';
import TimelineIcon from '@mui/icons-material/Timeline';
import axiosInstance from '../../lib/axios';
import { getUser } from '../../utils/auth';
import { palette, typography, theme } from '../../theme/colors';
import { ColorModeContext } from '../../contexts/ColorModeContext';
import { useAnimationStyles } from '../../hooks/useAnimations';
import { useModuleAccess } from '../../hooks/useModuleAccess';
import { BlurredFeatureOverlay } from '../../components/BlurredFeatureOverlay';
import { AnalyticsHeader } from '../../components/analytics/AnalyticsHeader';
import {
  ProfitAnalysisResponse,
  PeriodType,
} from '../../types/analytics';

interface ProfitAnalysisParams {
  organizationId?: string;
  locationId?: string;
  periodType: PeriodType;
  startDate?: string;
  endDate?: string;
  limit?: number;
}

const fetchProfitAnalysis = async (params: ProfitAnalysisParams): Promise<ProfitAnalysisResponse> => {
  const user = getUser();
  const queryParams: Record<string, any> = {
    periodType: params.periodType,
  };

  if (params.startDate) {
    queryParams.startDate = params.startDate;
  }
  if (params.endDate) {
    queryParams.endDate = params.endDate;
  }
  if (params.limit) {
    queryParams.limit = params.limit;
  }
  if (user?.locationId) {
    queryParams.locationId = user.locationId;
  }

  const response = await axiosInstance.get('/analytics/profit', { params: queryParams });
  return response.data;
};

export const ProfitAnalysis = () => {
  const { mode } = useContext(ColorModeContext);
  const animations = useAnimationStyles();
  const { hasAccess, isLoading: moduleLoading } = useModuleAccess('advanced_analytics');

  // Date range state - default to last 30 days
  const [endDate, setEndDate] = useState<Date>(new Date());
  const [startDate, setStartDate] = useState<Date>(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date;
  });
  const [selectedPreset, setSelectedPreset] = useState<string>('30d');

  // Determine period type based on date range
  const periodType = useMemo(() => {
    const days = Math.ceil(
      (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)
    );
    if (days <= 31) return PeriodType.DAILY;
    if (days <= 90) return PeriodType.WEEKLY;
    return PeriodType.MONTHLY;
  }, [startDate, endDate]);

  const {
    data: profitData,
    isLoading,
    error,
    dataUpdatedAt,
    isRefetching,
    refetch,
  } = useQuery({
    queryKey: ['profitAnalysis', startDate, endDate, periodType],
    queryFn: () =>
      fetchProfitAnalysis({
        periodType,
        startDate: startDate.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0],
        limit: 10,
      }),
    enabled: hasAccess && !moduleLoading,
    staleTime: 5 * 60 * 1000,       // Data fresh for 5 minutes
    refetchInterval: 5 * 60 * 1000, // Auto-refresh every 5 minutes
  });

  const handlePresetClick = (preset: string) => {
    const end = new Date();
    const start = new Date();

    switch (preset) {
      case '7d':
        start.setDate(start.getDate() - 7);
        break;
      case '30d':
        start.setDate(start.getDate() - 30);
        break;
      case '90d':
        start.setDate(start.getDate() - 90);
        break;
      case '1y':
        start.setFullYear(start.getFullYear() - 1);
        break;
    }

    setStartDate(start);
    setEndDate(end);
    setSelectedPreset(preset);
  };

  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setStartDate(new Date(e.target.value));
    setSelectedPreset('custom');
  };

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEndDate(new Date(e.target.value));
    setSelectedPreset('custom');
  };

  const colors =
    mode === 'dark'
      ? {
          bg: palette.background,
          surface: palette.surface,
          surfaceElevated: palette.surfaceElevated,
          border: palette.border,
          borderLight: palette.borderLight,
          textPrimary: palette.textPrimary,
          textSecondary: palette.textSecondary,
          textMuted: palette.textMuted,
        }
      : {
          bg: palette.light.background,
          surface: palette.light.surface,
          surfaceElevated: palette.light.surfaceElevated,
          border: palette.light.border,
          borderLight: palette.light.borderLight,
          textPrimary: palette.light.textPrimary,
          textSecondary: palette.light.textSecondary,
          textMuted: palette.light.textMuted,
        };

  const cardStyles = {
    backgroundColor: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: theme.radius.xl,
    p: 3,
    transition: theme.transitions.normal,
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-DZ', {
      style: 'currency',
      currency: 'DZD',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-DZ', {
      month: 'short',
      day: 'numeric',
    });
  };

  const formatDateForInput = (date: Date) => {
    return date.toISOString().split('T')[0];
  };

  const getDaysRange = () => {
    const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  // Handle module access loading
  if (moduleLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress sx={{ color: palette.primary }} />
      </Box>
    );
  }

  // Prepare chart data
  const chartData = profitData?.byPeriod || [];

  const pageContent = (
    <Box sx={{ ...animations.fadeIn }}>
      {/* Page Header */}
      <AnalyticsHeader
        category="Profit Analytics"
        title="Profit Analysis"
        subtitle={`Analyze profitability and margins across ${getDaysRange()} days`}
        dataUpdatedAt={dataUpdatedAt}
        isRefetching={isRefetching}
        onRefresh={() => refetch()}
      />

      {/* Date Range Picker */}
      <Box
        sx={{
          ...cardStyles,
          mb: 3,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2,
          alignItems: { xs: 'stretch', md: 'center' },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CalendarTodayOutlinedIcon sx={{ color: colors.textMuted, fontSize: 20 }} />
          <Typography
            variant="body2"
            sx={{
              fontWeight: typography.fontWeight.semibold,
              color: colors.textPrimary,
            }}
          >
            Time Period
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, flex: 1, flexWrap: 'wrap' }}>
          <ButtonGroup
            variant="outlined"
            sx={{
              '& .MuiButton-root': {
                borderColor: colors.border,
                color: colors.textSecondary,
                textTransform: 'none',
                fontWeight: typography.fontWeight.medium,
                fontSize: typography.fontSize.sm,
                '&:hover': {
                  borderColor: palette.primary,
                  backgroundColor: palette.primaryMuted,
                },
              },
            }}
          >
            {['7d', '30d', '90d', '1y'].map((preset) => (
              <Button
                key={preset}
                onClick={() => handlePresetClick(preset)}
                sx={{
                  ...(selectedPreset === preset && {
                    backgroundColor: palette.primaryMuted,
                    borderColor: `${palette.primary} !important`,
                    color: palette.primary,
                  }),
                }}
              >
                {preset === '7d' && 'Last 7 days'}
                {preset === '30d' && 'Last 30 days'}
                {preset === '90d' && 'Last 90 days'}
                {preset === '1y' && 'Last year'}
              </Button>
            ))}
          </ButtonGroup>

          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
            <TextField
              type="date"
              label="Start Date"
              value={formatDateForInput(startDate)}
              onChange={handleStartDateChange}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: colors.surfaceElevated,
                  '& fieldset': { borderColor: colors.border },
                  '&:hover fieldset': { borderColor: colors.borderLight },
                  '&.Mui-focused fieldset': { borderColor: palette.primary },
                },
                '& .MuiInputLabel-root': { color: colors.textMuted },
                '& .MuiInputBase-input': { color: colors.textPrimary },
              }}
            />
            <Typography variant="body2" sx={{ color: colors.textMuted }}>
              to
            </Typography>
            <TextField
              type="date"
              label="End Date"
              value={formatDateForInput(endDate)}
              onChange={handleEndDateChange}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: colors.surfaceElevated,
                  '& fieldset': { borderColor: colors.border },
                  '&:hover fieldset': { borderColor: colors.borderLight },
                  '&.Mui-focused fieldset': { borderColor: palette.primary },
                },
                '& .MuiInputLabel-root': { color: colors.textMuted },
                '& .MuiInputBase-input': { color: colors.textPrimary },
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* Period Type Indicator */}
      <Box
        sx={{
          ...cardStyles,
          mb: 3,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          alignItems: { xs: 'stretch', sm: 'center' },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TimelineIcon sx={{ color: colors.textMuted, fontSize: 20 }} />
          <Typography
            variant="body2"
            sx={{
              fontWeight: typography.fontWeight.semibold,
              color: colors.textPrimary,
            }}
          >
            Analysis Granularity
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ color: colors.textSecondary }}>
          {periodType.charAt(0).toUpperCase() + periodType.slice(1)} (auto-selected based on date range)
        </Typography>
      </Box>

      {/* Loading State */}
      {isLoading && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress sx={{ color: palette.primary }} />
        </Box>
      )}

      {/* Error State */}
      {error && (
        <Box p={3}>
          <Alert
            severity="error"
            sx={{
              borderRadius: theme.radius.lg,
              backgroundColor: palette.errorMuted,
              color: palette.error,
            }}
          >
            {error instanceof Error ? error.message : 'Failed to load profit analysis data'}
          </Alert>
        </Box>
      )}

      {/* Data Loaded */}
      {!isLoading && !error && profitData && (
        <>
          {/* Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} lg={3}>
              <Card
                sx={{
                  ...cardStyles,
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    backgroundColor: palette.primary,
                  },
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography
                      variant="overline"
                      sx={{
                        color: colors.textMuted,
                        fontFamily: typography.fontFamily.mono,
                        fontSize: typography.fontSize.xs,
                        letterSpacing: typography.letterSpacing.widest,
                      }}
                    >
                      Revenue
                    </Typography>
                    <Typography
                      variant="h5"
                      sx={{
                        fontFamily: typography.fontFamily.mono,
                        fontWeight: typography.fontWeight.extrabold,
                        color: colors.textPrimary,
                        mt: 1,
                      }}
                    >
                      {formatCurrency(profitData.summary.revenue)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: colors.textMuted }}>
                      {profitData.summary.salesCount} sales
                    </Typography>
                  </Box>
                  <ShoppingCartIcon sx={{ fontSize: 40, color: alpha(palette.primary, 0.2) }} />
                </Box>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} lg={3}>
              <Card
                sx={{
                  ...cardStyles,
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    backgroundColor: palette.warning,
                  },
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography
                      variant="overline"
                      sx={{
                        color: colors.textMuted,
                        fontFamily: typography.fontFamily.mono,
                        fontSize: typography.fontSize.xs,
                        letterSpacing: typography.letterSpacing.widest,
                      }}
                    >
                      Cost of Goods
                    </Typography>
                    <Typography
                      variant="h5"
                      sx={{
                        fontFamily: typography.fontFamily.mono,
                        fontWeight: typography.fontWeight.extrabold,
                        color: palette.warning,
                        mt: 1,
                      }}
                    >
                      {formatCurrency(profitData.summary.cogs)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: colors.textMuted }}>
                      {profitData.summary.itemsSold} items sold
                    </Typography>
                  </Box>
                  <InventoryIcon sx={{ fontSize: 40, color: alpha(palette.warning, 0.2) }} />
                </Box>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} lg={3}>
              <Card
                sx={{
                  ...cardStyles,
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    backgroundColor:
                      profitData.summary.grossProfit >= 0 ? palette.success : palette.error,
                  },
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography
                      variant="overline"
                      sx={{
                        color: colors.textMuted,
                        fontFamily: typography.fontFamily.mono,
                        fontSize: typography.fontSize.xs,
                        letterSpacing: typography.letterSpacing.widest,
                      }}
                    >
                      Gross Profit
                    </Typography>
                    <Typography
                      variant="h5"
                      sx={{
                        fontFamily: typography.fontFamily.mono,
                        fontWeight: typography.fontWeight.extrabold,
                        color:
                          profitData.summary.grossProfit >= 0 ? palette.success : palette.error,
                        mt: 1,
                      }}
                    >
                      {profitData.summary.grossProfit > 0 ? '+' : ''}
                      {formatCurrency(profitData.summary.grossProfit)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: colors.textMuted }}>
                      Avg {formatCurrency(profitData.summary.averageMarginPerSale)} per sale
                    </Typography>
                  </Box>
                  {profitData.summary.grossProfit >= 0 ? (
                    <TrendingUpIcon sx={{ fontSize: 40, color: alpha(palette.success, 0.2) }} />
                  ) : (
                    <TrendingDownIcon sx={{ fontSize: 40, color: alpha(palette.error, 0.2) }} />
                  )}
                </Box>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} lg={3}>
              <Card
                sx={{
                  ...cardStyles,
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    backgroundColor: palette.info,
                  },
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography
                      variant="overline"
                      sx={{
                        color: colors.textMuted,
                        fontFamily: typography.fontFamily.mono,
                        fontSize: typography.fontSize.xs,
                        letterSpacing: typography.letterSpacing.widest,
                      }}
                    >
                      Gross Margin
                    </Typography>
                    <Typography
                      variant="h5"
                      sx={{
                        fontFamily: typography.fontFamily.mono,
                        fontWeight: typography.fontWeight.extrabold,
                        color: palette.info,
                        mt: 1,
                      }}
                    >
                      {profitData.summary.grossMarginPercent.toFixed(1)}%
                    </Typography>
                    <Typography variant="caption" sx={{ color: colors.textMuted }}>
                      Overall margin
                    </Typography>
                  </Box>
                  <PercentIcon sx={{ fontSize: 40, color: alpha(palette.info, 0.2) }} />
                </Box>
              </Card>
            </Grid>
          </Grid>

          {/* Profit Trend Chart */}
          <Box sx={{ ...cardStyles, mb: 3 }}>
            <Typography
              variant="h6"
              sx={{
                fontFamily: typography.fontFamily.display,
                fontWeight: typography.fontWeight.semibold,
                color: colors.textPrimary,
                mb: 3,
              }}
            >
              Profit Trend
            </Typography>
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={350}>
                <AreaChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={palette.primary} stopOpacity={0.3} />
                      <stop offset="95%" stopColor={palette.primary} stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="colorCogs" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={palette.warning} stopOpacity={0.3} />
                      <stop offset="95%" stopColor={palette.warning} stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="colorProfit" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={palette.success} stopOpacity={0.3} />
                      <stop offset="95%" stopColor={palette.success} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={colors.border} />
                  <XAxis
                    dataKey="period"
                    stroke={colors.textMuted}
                    tickFormatter={formatDate}
                    style={{ fontSize: typography.fontSize.xs }}
                  />
                  <YAxis stroke={colors.textMuted} style={{ fontSize: typography.fontSize.xs }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: colors.surface,
                      border: `1px solid ${colors.border}`,
                      borderRadius: theme.radius.md,
                      color: colors.textPrimary,
                    }}
                    labelStyle={{ color: colors.textPrimary }}
                    formatter={(value: any, name: string) => {
                      if (name === 'revenue') return [formatCurrency(value), 'Revenue'];
                      if (name === 'cogs') return [formatCurrency(value), 'COGS'];
                      if (name === 'profit') return [formatCurrency(value), 'Profit'];
                      return [value, name];
                    }}
                    labelFormatter={(label) => formatDate(label)}
                  />
                  <Legend
                    wrapperStyle={{ color: colors.textPrimary }}
                    formatter={(value) => {
                      if (value === 'revenue') return 'Revenue';
                      if (value === 'cogs') return 'COGS';
                      if (value === 'profit') return 'Profit';
                      return value;
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="revenue"
                    stroke={palette.primary}
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#colorRevenue)"
                  />
                  <Area
                    type="monotone"
                    dataKey="cogs"
                    stroke={palette.warning}
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#colorCogs)"
                  />
                  <Area
                    type="monotone"
                    dataKey="profit"
                    stroke={palette.success}
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#colorProfit)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: 350,
                }}
              >
                <Typography variant="body2" sx={{ color: colors.textMuted }}>
                  No trend data available. Try adjusting the date range.
                </Typography>
              </Box>
            )}
          </Box>

          {/* Tables Row */}
          <Grid container spacing={3}>
            {/* Top Profitable Products Table */}
            <Grid item xs={12} lg={6}>
              <Box sx={cardStyles}>
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: typography.fontFamily.display,
                    fontWeight: typography.fontWeight.semibold,
                    color: colors.textPrimary,
                    mb: 3,
                  }}
                >
                  Top Profitable Products
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell
                          sx={{ color: colors.textMuted, fontWeight: typography.fontWeight.semibold }}
                        >
                          Product
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ color: colors.textMuted, fontWeight: typography.fontWeight.semibold }}
                        >
                          Qty
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ color: colors.textMuted, fontWeight: typography.fontWeight.semibold }}
                        >
                          Profit
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ color: colors.textMuted, fontWeight: typography.fontWeight.semibold }}
                        >
                          Margin
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {profitData.topProfitableProducts && profitData.topProfitableProducts.length > 0 ? (
                        profitData.topProfitableProducts.map((product, index) => (
                          <TableRow
                            key={product.productId}
                            sx={{
                              '&:hover': { backgroundColor: colors.surfaceElevated },
                              transition: theme.transitions.fast,
                            }}
                          >
                            <TableCell sx={{ color: colors.textPrimary }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box
                                  sx={{
                                    width: 24,
                                    height: 24,
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor:
                                      index < 3 ? palette.successMuted : colors.surfaceElevated,
                                    color: index < 3 ? palette.success : colors.textMuted,
                                    fontWeight: typography.fontWeight.bold,
                                    fontSize: typography.fontSize.xs,
                                  }}
                                >
                                  {index + 1}
                                </Box>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: typography.fontWeight.medium,
                                    maxWidth: 150,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {product.productName}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{ color: colors.textPrimary, fontFamily: typography.fontFamily.mono }}
                            >
                              {product.quantitySold}
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                color: palette.success,
                                fontFamily: typography.fontFamily.mono,
                                fontWeight: typography.fontWeight.semibold,
                              }}
                            >
                              {formatCurrency(product.profit)}
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                color: colors.textSecondary,
                                fontFamily: typography.fontFamily.mono,
                              }}
                            >
                              {product.marginPercent.toFixed(1)}%
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={4} align="center" sx={{ py: 4, color: colors.textMuted }}>
                            No profit data available
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Grid>

            {/* Low Margin Products Table */}
            <Grid item xs={12} lg={6}>
              <Box sx={cardStyles}>
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: typography.fontFamily.display,
                    fontWeight: typography.fontWeight.semibold,
                    color: colors.textPrimary,
                    mb: 3,
                  }}
                >
                  Low Margin Products
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell
                          sx={{ color: colors.textMuted, fontWeight: typography.fontWeight.semibold }}
                        >
                          Product
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ color: colors.textMuted, fontWeight: typography.fontWeight.semibold }}
                        >
                          Qty
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ color: colors.textMuted, fontWeight: typography.fontWeight.semibold }}
                        >
                          Profit
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ color: colors.textMuted, fontWeight: typography.fontWeight.semibold }}
                        >
                          Margin
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {profitData.lowMarginProducts && profitData.lowMarginProducts.length > 0 ? (
                        profitData.lowMarginProducts.map((product, index) => (
                          <TableRow
                            key={product.productId}
                            sx={{
                              '&:hover': { backgroundColor: colors.surfaceElevated },
                              transition: theme.transitions.fast,
                            }}
                          >
                            <TableCell sx={{ color: colors.textPrimary }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box
                                  sx={{
                                    width: 24,
                                    height: 24,
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor:
                                      index < 3 ? palette.errorMuted : colors.surfaceElevated,
                                    color: index < 3 ? palette.error : colors.textMuted,
                                    fontWeight: typography.fontWeight.bold,
                                    fontSize: typography.fontSize.xs,
                                  }}
                                >
                                  {index + 1}
                                </Box>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: typography.fontWeight.medium,
                                    maxWidth: 150,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {product.productName}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{ color: colors.textPrimary, fontFamily: typography.fontFamily.mono }}
                            >
                              {product.quantitySold}
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                color: product.profit >= 0 ? palette.warning : palette.error,
                                fontFamily: typography.fontFamily.mono,
                                fontWeight: typography.fontWeight.semibold,
                              }}
                            >
                              {formatCurrency(product.profit)}
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                color: product.marginPercent >= 10 ? palette.warning : palette.error,
                                fontFamily: typography.fontFamily.mono,
                              }}
                            >
                              {product.marginPercent.toFixed(1)}%
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={4} align="center" sx={{ py: 4, color: colors.textMuted }}>
                            No low margin products found
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Grid>
          </Grid>
        </>
      )}

      {/* Empty State */}
      {!isLoading && !error && !profitData && (
        <Box sx={cardStyles}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 8,
            }}
          >
            <TrendingUpIcon sx={{ fontSize: 64, color: colors.textMuted, mb: 2 }} />
            <Typography variant="h6" sx={{ color: colors.textPrimary, mb: 1 }}>
              No Profit Data Available
            </Typography>
            <Typography variant="body2" sx={{ color: colors.textSecondary, textAlign: 'center', maxWidth: 400 }}>
              There isn&apos;t enough sales data to generate a profit analysis. Continue making
              sales and check back later.
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );

  // Wrap content with BlurredFeatureOverlay for non-PROFESSIONAL users
  return (
    <BlurredFeatureOverlay feature="Profit Analysis" requiredTier="professional">
      {pageContent}
    </BlurredFeatureOverlay>
  );
};

export default ProfitAnalysis;
